/* =============================================
   BookTests.jsx — TANBIO
   Controlled popup modal to book diagnostic tests.
   Split layout: left image panel + right form panel.
   Validates patient details + selected tests,
   then redirects to WhatsApp with the booking
   summary pre-filled.
   ============================================= */
import React, { useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import SERVICES from "../data/services.data";
import bookTestImg from "../assets/contact-girl.png";
import "../styles/BookTests.css";

const WHATSAPP_NUMBER = "917358798182";

const getTodayDate = () => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export default function BookTests({ show, onClose }) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    age: "",
    gender: "",
    date: "",
  });
  const [selectedTests, setSelectedTests] = useState([]);
  const [errors, setErrors] = useState({});

  const todayDate = getTodayDate();

  const handleClose = () => {
    onClose();
    setFormData({ name: "", phone: "", age: "", gender: "", date: "" });
    setSelectedTests([]);
    setErrors({});
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    if (/^[A-Za-z\s]*$/.test(value)) {
      setFormData((prev) => ({ ...prev, name: value }));
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    if (/^\d{0,10}$/.test(value)) {
      setFormData((prev) => ({ ...prev, phone: value }));
    }
  };

  const handleAgeChange = (e) => {
    const value = e.target.value;
    if (/^\d{0,3}$/.test(value)) {
      setFormData((prev) => ({ ...prev, age: value }));
    }
  };

  const handleGenderChange = (e) => {
    setFormData((prev) => ({ ...prev, gender: e.target.value }));
  };

  const handleDateChange = (e) => {
    setFormData((prev) => ({ ...prev, date: e.target.value }));
  };

  const handleTestToggle = (testName) => {
    setSelectedTests((prev) =>
      prev.includes(testName)
        ? prev.filter((t) => t !== testName)
        : [...prev, testName]
    );
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Patient name is required.";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required.";
    } else if (formData.phone.length !== 10) {
      newErrors.phone = "Enter a valid 10-digit phone number.";
    }

    if (!formData.age.trim()) {
      newErrors.age = "Age is required.";
    } else if (Number(formData.age) <= 0 || Number(formData.age) > 120) {
      newErrors.age = "Enter a valid age.";
    }

    if (!formData.gender) {
      newErrors.gender = "Please select a gender.";
    }

    if (!formData.date) {
      newErrors.date = "Please select a preferred date.";
    }

    if (selectedTests.length === 0) {
      newErrors.tests = "Please select at least one test.";
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const testsList = selectedTests.map((t) => `- ${t}`).join("\n");

    const message =
      `*New Test Booking Request*\n\n` +
      `*Patient Name:* ${formData.name}\n` +
      `*Phone Number:* ${formData.phone}\n` +
      `*Age:* ${formData.age}\n` +
      `*Gender:* ${formData.gender}\n` +
      `*Preferred Date:* ${formData.date}\n\n` +
      `*Tests Required:*\n${testsList}`;

    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
      message
    )}`;

    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
    handleClose();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      contentClassName="bt-modal-content"
      dialogClassName="bt-modal-dialog"
    >
      <div className="bt-modal-overlay">
        <div className="bt-layout">

          {/* ── Left image panel (non-scrollable) ── */}
          <div className="bt-left-panel">
            <img src={bookTestImg} alt="Book a diagnostic test" loading="lazy" className="bt-left-img" />
          </div>

          {/* ── Right form panel ── */}
          <div className="bt-right-panel">

            {/* Header (non-scrollable) */}
            <div className="bt-header">
              <h3 className="bt-title">
                <i className="fa-solid fa-clipboard-list me-2"></i>
                Book Your Test
              </h3>
              <button
                type="button"
                className="bt-close-btn"
                onClick={handleClose}
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            {/* Scrollable form area */}
            <div className="bt-body-scroll">
              <Form noValidate onSubmit={handleSubmit} id="bt-form">
                <Row className="g-3">

                  <Col xs={12} md={6}>
                    <Form.Label className="bt-label">Patient Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter patient name"
                      value={formData.name}
                      onChange={handleNameChange}
                      isInvalid={!!errors.name}
                      className="bt-input"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.name}
                    </Form.Control.Feedback>
                  </Col>

                  <Col xs={12} md={6}>
                    <Form.Label className="bt-label">Phone Number</Form.Label>
                    <Form.Control
                      type="tel"
                      inputMode="numeric"
                      placeholder="Enter 10-digit phone number"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      isInvalid={!!errors.phone}
                      className="bt-input"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.phone}
                    </Form.Control.Feedback>
                  </Col>

                  <Col xs={12} md={4}>
                    <Form.Label className="bt-label">Age</Form.Label>
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      placeholder="Enter age"
                      value={formData.age}
                      onChange={handleAgeChange}
                      isInvalid={!!errors.age}
                      className="bt-input"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.age}
                    </Form.Control.Feedback>
                  </Col>

                  <Col xs={12} md={4}>
                    <Form.Label className="bt-label">Gender</Form.Label>
                    <Form.Select
                      value={formData.gender}
                      onChange={handleGenderChange}
                      isInvalid={!!errors.gender}
                      className="bt-input"
                    >
                      <option value="">Select gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Others">Others</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.gender}
                    </Form.Control.Feedback>
                  </Col>

                  <Col xs={12} md={4}>
                    <Form.Label className="bt-label">Preferred Date</Form.Label>
                    <Form.Control
                      type="date"
                      min={todayDate}
                      value={formData.date}
                      onChange={handleDateChange}
                      isInvalid={!!errors.date}
                      className="bt-input"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.date}
                    </Form.Control.Feedback>
                  </Col>

                  <Col xs={12}>
                    <Form.Label className="bt-label">
                      Select Tests{" "}
                      <span className="bt-hint">(you can select multiple)</span>
                    </Form.Label>

                    <div
                      className={`bt-tests-wrap ${
                        errors.tests ? "bt-tests-invalid" : ""
                      }`}
                    >
                      {SERVICES.map((svc) => (
                        <div key={svc.id} className="bt-category">
                          <p className="bt-category-title">
                            <i className={`${svc.icon} me-2`}></i>
                            {svc.title}
                          </p>
                          <Row className="g-2">
                            {svc.tests.map((t, idx) => {
                              const testName = typeof t === "string" ? t : t.name;
                              const checkId = `bt-${svc.id}-${idx}`;
                              return (
                                <Col xs={12} sm={6} lg={4} key={checkId}>
                                  <Form.Check
                                    type="checkbox"
                                    id={checkId}
                                    label={testName}
                                    checked={selectedTests.includes(testName)}
                                    onChange={() => handleTestToggle(testName)}
                                    className="bt-check"
                                  />
                                </Col>
                              );
                            })}
                          </Row>
                        </div>
                      ))}
                    </div>

                    {errors.tests && (
                      <div className="bt-tests-error">{errors.tests}</div>
                    )}
                  </Col>
                </Row>
              </Form>
            </div>

            {/* Footer with submit button (non-scrollable) */}
            <div className="bt-footer">
              <Button type="submit" form="bt-form" className="bt-submit-btn">
                <i className="fa-brands fa-whatsapp me-2"></i>
                Book Now
              </Button>
            </div>

          </div>
        </div>
      </div>
    </Modal>
  );
}